/**
 * Settings → Communication → API tokens (G20 public API / MCP auth).
 *
 * Vite SPA port of:
 *   frontend/src/app/(dashboard)/dashboard/settings/api-tokens/page.tsx
 *   frontend/src/hooks/use-api-tokens.ts
 *   frontend/src/lib/api/api-tokens.ts
 *
 * Self-service management of personal Bearer tokens that authenticate the
 * WorkRight MCP server AND the G20 public API (/api/v1/public/*). ANY role
 * (not just HR admins) can manage their own tokens — the backend enforces
 * (tenant_id, user_id) ownership so a token can only reach the creator's
 * tenant, and per-route role gates on the public API still apply (e.g. a
 * non-HR token is 403'd from /public/employees).
 *
 * UX contract:
 *   - Create: name + capability picker → server returns raw_token ONCE,
 *     shown in a one-time reveal dialog (never persisted to state after
 *     dismissal — the user must copy it).
 *   - List: name, capabilities, created/last-used/revoked timestamps.
 *     Active tokens first; revoked ones in a collapsed <details>.
 *   - Revoke: AlertDialog confirm → DELETE /{id}.
 *   - MCP connection instructions shown below the list.
 *
 * Migration note (Wave H, 2026-06-06): direct port following Vite SPA
 * conventions — TanStack Router createFileRoute, SettingsShell +
 * SettingsPageHeader wrapper, inline react-query hooks. NOT HR-gated
 * (tokens are user-scoped) — useHrRoles() only hides the HR-only
 * "Payroll (read)" capability option (wave 2; the server refuses it for
 * other roles anyway).
 *
 * Plan gates (pricing 2026-10-22): the hosted MCP server is on EVERY plan
 * (`mcp`); the REST API (/api/v1/public/*) and webhooks are WorkRight Pro
 * (`public_api`). "New token" shows when the tenant has either. A tenant
 * with MCP but not the REST API sees a plain note that REST calls are
 * Pro, and the REST instructions are hidden. With neither, the page says
 * so and still lists existing tokens so they can be revoked.
 *
 * Security (hosted MCP review, 2026-10): every token expires (30 / 90 /
 * 365 days, default 90; "Never" only for a read-only token); creating ANY
 * token needs a fresh 2FA code (the server answers 401 totp_required and
 * the shared step-up prompt asks for it in place, then replays); at most
 * 10 active tokens; a password change / reset or deactivation revokes
 * them all.
 *
 * MCP: AI clients connect to the hosted endpoint `https://api.workright.in/mcp`
 * (Streamable HTTP) with a token from this page as an
 * `Authorization: Bearer` header — nothing to install, and never a
 * database address. The card shows the endpoint, a copyable Claude Code
 * command and a Cursor / generic JSON config (src/lib/mcp-connect.ts).
 * The endpoint needs the `mcp` feature (every plan), so the card shows
 * the snippets on Free too.
 */

import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Bot,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  Terminal,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { fmtDate, fmtDateTime } from "@/lib/leave-format";
import { toApiError } from "@/lib/api";
import { isTotpEnrollmentRequired } from "@/lib/api/client";
import { API_BASE_URL } from "@/lib/api/client";
import {
  CURSOR_USER_CONFIG_PATH,
  claudeCodeCommand,
  mcpEndpointUrl,
  mcpJsonConfig,
} from "@/lib/mcp-connect";
import { useFeatures } from "@/lib/auth/auth-context";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  listApiTokens,
  createApiToken,
  revokeApiToken,
  hasElevatedCapability,
  isTokenExpired,
  type ApiToken,
  type CreateApiTokenRequest,
  type TokenCapability,
  type TokenExpiryDays,
} from "@/lib/api/api-tokens";
import { EmptyState } from "@/components/shared/empty-state";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/api-tokens",
)({
  component: SettingsApiTokensRoute,
});

// ---------------------------------------------------------------------------
// Query keys + hooks
// ---------------------------------------------------------------------------

const apiTokenKeys = {
  all: ["api-tokens"] as const,
  list: () => [...apiTokenKeys.all, "list"] as const,
};

function useApiTokens() {
  return useQuery({
    queryKey: apiTokenKeys.list(),
    queryFn: ({ signal }) => listApiTokens(signal),
    staleTime: 30 * 1000,
  });
}

function useCreateApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateApiTokenRequest) => createApiToken(body),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: apiTokenKeys.list() }),
  });
}

function useRevokeApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => revokeApiToken(id),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: apiTokenKeys.list() }),
  });
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CAP_OPTIONS: {
  value: TokenCapability;
  label: string;
  description: string;
  /** Only HR managers / company admins may mint it (server-enforced). */
  hrOnly?: boolean;
}[] = [
  {
    value: "read",
    label: "Read",
    description:
      "GET endpoints only (employees, leave, expenses, expense batches, accounting)",
  },
  {
    value: "write",
    label: "Write",
    description: "POST, PATCH, DELETE on entity-scoped resources",
  },
  {
    value: "admin",
    label: "Admin",
    description:
      "Everything above, plus creating and changing webhook subscriptions",
  },
  {
    value: "payroll.view",
    label: "Payroll (Read)",
    description:
      "Public API: locked payroll runs, including salaries. Admin does not include it, and on its own it allows no other endpoint.",
    hrOnly: true,
  },
];

const EXPIRY_OPTIONS: { value: TokenExpiryDays; label: string }[] = [
  { value: 30, label: "30 Days" },
  { value: 90, label: "90 Days" },
  { value: 365, label: "1 Year" },
  { value: 0, label: "Never (Read-Only Tokens Only)" },
];

// ---------------------------------------------------------------------------
// CreateTokenDialog
// ---------------------------------------------------------------------------

function CreateTokenDialog({
  onCreated,
}: {
  readonly onCreated: (rawToken: string, name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [caps, setCaps] = useState<TokenCapability[]>(["read"]);
  const [expiry, setExpiry] = useState<TokenExpiryDays>(90);
  const create = useCreateApiToken();
  const navigate = useNavigate();
  const tenantSlug = useTenantSlug();
  const elevated = hasElevatedCapability(caps);
  // "Never" is for read-only tokens only (server-enforced too).
  const effectiveExpiry: TokenExpiryDays =
    elevated && expiry === 0 ? 90 : expiry;
  const { isHrAdmin } = useHrRoles();
  const capOptions = CAP_OPTIONS.filter((o) => !o.hrOnly || isHrAdmin);

  function toggleCap(cap: TokenCapability) {
    setCaps((prev) =>
      prev.includes(cap) ? prev.filter((c) => c !== cap) : [...prev, cap],
    );
  }

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    const selectedCaps: TokenCapability[] =
      caps.length > 0 ? caps : ["read"];
    create.mutate(
      {
        name: name.trim(),
        capabilities: selectedCaps,
        expires_in_days: effectiveExpiry,
      },
      {
        onSuccess: (res) => {
          setOpen(false);
          setName("");
          setCaps(["read"]);
          setExpiry(90);
          onCreated(res.raw_token, res.token.name);
        },
        onError: (err) => {
          // No 2FA yet: say so, with one action to set it up.
          if (isTotpEnrollmentRequired(err)) {
            toast.error(toApiError(err).message, {
              action: {
                label: "Set Up 2FA",
                onClick: () =>
                  void navigate({
                    to: "/app/$tenantSlug/me/account/security",
                    params: { tenantSlug },
                  }),
              },
            });
            return;
          }
          toast.error(toApiError(err).message);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid="api-token-add-btn">
          <Plus aria-hidden className="h-4 w-4" />
          New Token
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create API Token</DialogTitle>
            <DialogDescription>
              Give this token a label and choose what it can access. The token
              value will be shown once after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="token-name">Token Name</Label>
              <Input
                id="token-name"
                placeholder="e.g. Claude Desktop, Tally integration"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                required
                autoFocus
                data-testid="api-token-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Capabilities</Label>
              <div className="space-y-2 rounded-md border p-3">
                {capOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-start gap-3 text-sm"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                      checked={caps.includes(opt.value)}
                      onChange={() => toggleCap(opt.value)}
                      data-testid={`api-token-cap-${opt.value}`}
                    />
                    <span>
                      <span className="font-medium">{opt.label}</span>
                      <span className="block text-xs text-muted-foreground">
                        {opt.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Select at least one capability. &quot;Write&quot; implies
                &quot;Read&quot;. For a payroll integration, create a separate
                token with only &quot;Payroll (read)&quot;.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="token-expiry">Expires After</Label>
              <Select
                value={String(effectiveExpiry)}
                onValueChange={(v) => setExpiry(Number(v) as TokenExpiryDays)}
              >
                <SelectTrigger id="token-expiry" data-testid="api-token-expiry">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={String(o.value)}
                      disabled={o.value === 0 && elevated}
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {elevated
                  ? "A token that can change data or read payroll must expire. "
                  : ""}
                You&apos;ll be asked for your 2FA code to create a token.
              </p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || caps.length === 0 || create.isPending}
              data-testid="api-token-submit-btn"
            >
              {create.isPending && (
                <Loader2 aria-hidden className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              Create Token
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RevealTokenDialog — one-time raw-token reveal
// ---------------------------------------------------------------------------

function RevealTokenDialog({
  rawToken,
  tokenName,
  onClose,
}: {
  readonly rawToken: string;
  readonly tokenName: string;
  readonly onClose: () => void;
}) {
  const [visible, setVisible] = useState(false);

  function copyToken() {
    void navigator.clipboard.writeText(rawToken);
    toast.success("Token copied to clipboard");
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound aria-hidden className="h-4 w-4 text-primary" />
            Token Created: {tokenName}
          </DialogTitle>
          <DialogDescription>
            Copy this token now. It will not be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 rounded-md border bg-muted px-3 py-2">
          <div className="flex items-center gap-2">
            <code
              className="flex-1 break-all font-mono text-xs"
              data-testid="api-token-raw-value"
            >
              {visible ? rawToken : rawToken.replace(/./g, "•")}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "Hide token" : "Show token"}
            >
              {visible ? (
                <EyeOff aria-hidden className="h-4 w-4" />
              ) : (
                <Eye aria-hidden className="h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={copyToken}
              aria-label="Copy token"
            >
              <Copy aria-hidden className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Store this token in a password manager or your MCP client
          configuration. Once you close this dialog it cannot be recovered —
          you would need to revoke and create a new token.
        </p>
        <DialogFooter>
          <Button onClick={onClose}>Done — I have copied the token</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// TokenRow
// ---------------------------------------------------------------------------

function TokenRow({
  token,
  onRevoke,
  revoking,
}: {
  readonly token: ApiToken;
  readonly onRevoke: (id: number) => void;
  readonly revoking: boolean;
}) {
  const isRevoked = token.revoked_at != null;
  const isExpired = !isRevoked && isTokenExpired(token);

  return (
    <div
      className="flex items-start justify-between gap-4 rounded-md border px-4 py-3"
      data-testid={`api-token-row-${token.id}`}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{token.name}</span>
          {isRevoked ? (
            <Badge variant="destructive" className="text-xs">
              Revoked
            </Badge>
          ) : isExpired ? (
            <Badge variant="secondary" className="text-xs">
              Expired
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">
              Active
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          <span>Created {fmtDate(token.created_at)}</span>
          {token.last_used_at && (
            <span>Last used {fmtDateTime(token.last_used_at)}</span>
          )}
          {token.revoked_at && <span>Revoked {fmtDate(token.revoked_at)}</span>}
          {!isRevoked && (
            <span data-testid={`api-token-expiry-${token.id}`}>
              {token.expires_at
                ? `${isExpired ? "Expired" : "Expires"} ${fmtDate(token.expires_at)}`
                : "Never expires"}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1 pt-0.5">
          {token.capabilities.map((cap) => (
            <Badge
              key={cap}
              variant="secondary"
              className="text-xs font-normal"
            >
              {CAP_OPTIONS.find((o) => o.value === cap)?.label ?? cap}
            </Badge>
          ))}
        </div>
      </div>
      {!isRevoked && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={revoking}
              aria-label={`Revoke token "${token.name}"`}
              className="shrink-0 text-destructive hover:text-destructive"
              data-testid={`api-token-revoke-btn-${token.id}`}
            >
              {revoking ? (
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 aria-hidden className="h-4 w-4" />
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke token?</AlertDialogTitle>
              <AlertDialogDescription>
                The token <strong>{token.name}</strong> will be permanently
                revoked. Any MCP server, integration, or agent using it will
                stop working immediately. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onRevoke(token.id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Revoke Token
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MCPInstructions
// ---------------------------------------------------------------------------

function RestAPIInstructions({
  hasActiveToken,
}: {
  readonly hasActiveToken: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Terminal aria-hidden className="h-4 w-4 text-primary" />
          Calling the Public REST API
        </CardTitle>
        <CardDescription>
          Send a token as a Bearer header to any{" "}
          <code className="rounded bg-muted px-1 text-foreground">/api/v1/public/*</code>{" "}
          endpoint.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {!hasActiveToken && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            Create a token above to get started.
          </p>
        )}
        <pre className="overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs">{`curl -H "Authorization: Bearer <your-token>" \\
  https://api.workright.in/api/v1/public/employees`}</pre>
        <div className="space-y-1">
          <p className="font-medium">What a token can reach</p>
          <ul className="list-inside list-disc space-y-0.5 text-muted-foreground">
            <li>Employees — directory roster (HR-admin token)</li>
            <li>Leave requests — filter by status and date range</li>
            <li>Expenses and expense batches</li>
            <li>Invoices and bills — accounting receivables and payables</li>
            <li>Webhooks — manage outbound event subscriptions (Admin)</li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Each call is limited by the token&apos;s capabilities and by your
            own role in this workspace.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/** A read-only snippet with a copy button. */
function CopySnippet({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}) {
  function copy() {
    void navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={copy}
          aria-label={`Copy ${label}`}
        >
          <Copy aria-hidden className="mr-1 h-3.5 w-3.5" />
          Copy
        </Button>
      </div>
      <pre
        tabIndex={0}
        data-testid={testId}
        className="overflow-x-auto whitespace-pre rounded-md bg-muted px-3 py-2 font-mono text-xs"
      >
        {value}
      </pre>
    </div>
  );
}

/**
 * MCP (AI assistants): the hosted endpoint + client snippets. The token is
 * the only credential; the page never asks for a database address or a
 * local install (see the file comment).
 */
function MCPCard({ mcpEnabled }: { readonly mcpEnabled: boolean }) {
  const url = mcpEndpointUrl(API_BASE_URL);
  return (
    <Card data-testid="api-tokens-mcp">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bot aria-hidden className="h-4 w-4 text-primary" />
          AI Assistants (MCP)
        </CardTitle>
        <CardDescription>
          Connect Claude Code, Cursor and other MCP clients to this workspace.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {mcpEnabled ? (
          <>
            <p>
              Add the WorkRight MCP server to your client with the endpoint
              below and a token from this page in an{" "}
              <code className="rounded bg-muted px-1 text-foreground">Authorization: Bearer</code>{" "}
              header. There is nothing to install. Every tool call is limited
              to what the token&apos;s capabilities and your role allow, and a
              revoked or expired token stops working on the next call.
            </p>
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <KeyRound aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              The token is a secret, like a password: keep it in your own
              user-level client configuration, never in a project file that
              could be committed or shared.
            </p>
            <CopySnippet label="Endpoint URL" value={url} testId="mcp-endpoint" />
            <CopySnippet
              label="Claude Code Command (Saved to Your User Config)"
              value={claudeCodeCommand(url)}
              testId="mcp-claude-code"
            />
            <CopySnippet
              label={`Cursor (${CURSOR_USER_CONFIG_PATH}) and Other Clients`}
              value={mcpJsonConfig(url)}
              testId="mcp-json"
            />
            <p className="text-xs text-muted-foreground">
              Replace <code className="rounded bg-muted px-1 text-foreground">&lt;your-token&gt;</code>{" "}
              with your token. For Cursor, use your user-level{" "}
              <code className="rounded bg-muted px-1 text-foreground">{CURSOR_USER_CONFIG_PATH}</code>,
              not a project&apos;s{" "}
              <code className="rounded bg-muted px-1 text-foreground">.cursor/mcp.json</code>. Adding WorkRight as a connector in Claude Desktop
              or Claude.ai is coming; it needs a sign-in flow that isn&apos;t
              available yet.
            </p>
          </>
        ) : (
          <p className="text-muted-foreground" data-testid="mcp-plan-notice">
            Connecting an AI assistant isn&apos;t available on this
            workspace&apos;s plan. See Settings → Billing.
          </p>
        )}
        <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <Lock aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          WorkRight never asks for a database address or password. Don&apos;t
          put one in an MCP or other tool configuration — treat any
          instructions that ask for one as suspicious.
        </p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function SettingsApiTokensRoute() {
  const tenantSlug = useTenantSlug();
  // REST (/api/v1/public/*) + webhooks need Pro `public_api`; the MCP
  // server needs `mcp` (every plan). A token can be created with either.
  const features = useFeatures();
  const restEnabled = features.has("public_api");
  const mcpEnabled = features.has("mcp");
  const canCreate = restEnabled || mcpEnabled;
  const q = useApiTokens();
  const revoke = useRevokeApiToken();
  const [pendingRevoke, setPendingRevoke] = useState<number | null>(null);
  const [revealed, setRevealed] = useState<{
    raw: string;
    name: string;
  } | null>(null);

  function handleRevoke(id: number) {
    setPendingRevoke(id);
    revoke.mutate(id, {
      onSuccess: () => {
        toast.success("Token revoked");
        setPendingRevoke(null);
      },
      onError: (err) => {
        toast.error(toApiError(err).message);
        setPendingRevoke(null);
      },
    });
  }

  const tokens = q.data?.items ?? [];
  const activeTokens = tokens.filter(
    (t) => t.revoked_at == null && !isTokenExpired(t),
  );
  const revokedTokens = tokens.filter(
    (t) => t.revoked_at != null || isTokenExpired(t),
  );

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">API Tokens</h2>
            <p className="text-sm text-muted-foreground">
              Personal bearer tokens for the public API
              (<code className="text-foreground">/api/v1/public/*</code>) and the WorkRight MCP server.
              Each token is scoped to your account and this workspace.
            </p>
          </div>

          {canCreate && !restEnabled && (
            <Alert data-testid="api-tokens-rest-pro-notice">
              <Lock aria-hidden className="h-4 w-4" />
              <AlertTitle>Your tokens work with AI assistants (MCP)</AlertTitle>
              <AlertDescription>
                The REST API (<code className="text-foreground">/api/v1/public/*</code>) and webhooks are
                part of WorkRight Pro, so REST calls are refused on your plan.{" "}
                <Link
                  to="/app/$tenantSlug/settings/billing"
                  params={{ tenantSlug }}
                  className="font-medium underline underline-offset-2"
                >
                  See plans in Settings → Billing
                </Link>
                .
              </AlertDescription>
            </Alert>
          )}

          {!canCreate && (
            <Alert data-testid="api-tokens-plan-notice">
              <Lock aria-hidden className="h-4 w-4" />
              <AlertTitle>API tokens aren&apos;t on your plan</AlertTitle>
              <AlertDescription>
                New tokens can&apos;t be created here. Tokens you already have
                are listed below so you can revoke them.{" "}
                <Link
                  to="/app/$tenantSlug/settings/billing"
                  params={{ tenantSlug }}
                  className="font-medium underline underline-offset-2"
                >
                  See plans in Settings → Billing
                </Link>
                .
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader className="flex flex-col gap-3 space-y-0 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2 text-base">
                  <KeyRound aria-hidden className="h-4 w-4 text-primary" />
                  Your Tokens
                </CardTitle>
                <CardDescription>
                  Revoke tokens you no longer use. A revoked or expired token
                  stops working immediately, and changing your password
                  revokes all of them.
                </CardDescription>
              </div>
              {canCreate && (
                <CreateTokenDialog
                  onCreated={(raw, name) => setRevealed({ raw, name })}
                />
              )}
            </CardHeader>
            <CardContent>
              {q.isLoading && (
                <div className="space-y-2">
                  {["t1", "t2"].map((k) => (
                    <Skeleton key={k} className="h-16 w-full rounded-md" />
                  ))}
                </div>
              )}
              {q.isError && (
                <p className="text-sm text-destructive">
                  {toApiError(q.error).message}
                </p>
              )}
              {!q.isLoading && !q.isError && activeTokens.length === 0 && (
                <EmptyState
                  icon={KeyRound}
                  title="No Active Tokens"
                  description={
                    canCreate
                      ? restEnabled
                        ? "Personal bearer tokens authenticate the public REST API (/api/v1/public/*) and the WorkRight MCP server. Use “New token” above to create your first one."
                        : "A token lets your AI assistant (Claude Code, Cursor) work with this workspace. Use “New token” above to create your first one."
                      : "Tokens become available with a plan that includes them."
                  }
                />
              )}
              {!q.isLoading && !q.isError && activeTokens.length > 0 && (
                <div className="space-y-2" data-testid="api-tokens-list">
                  {activeTokens.map((token) => (
                    <TokenRow
                      key={token.id}
                      token={token}
                      onRevoke={handleRevoke}
                      revoking={pendingRevoke === token.id}
                    />
                  ))}
                </div>
              )}
              {revokedTokens.length > 0 && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    {revokedTokens.length} revoked or expired token
                    {revokedTokens.length !== 1 ? "s" : ""}
                  </summary>
                  <div className="mt-2 space-y-2 opacity-60">
                    {revokedTokens.map((token) => (
                      <TokenRow
                        key={token.id}
                        token={token}
                        onRevoke={handleRevoke}
                        revoking={false}
                      />
                    ))}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>

          {restEnabled && (
            <RestAPIInstructions hasActiveToken={activeTokens.length > 0} />
          )}
          <MCPCard mcpEnabled={mcpEnabled} />

          {revealed && (
            <RevealTokenDialog
              rawToken={revealed.raw}
              tokenName={revealed.name}
              onClose={() => setRevealed(null)}
            />
          )}
        </div>
      </SettingsShell>
    </>
  );
}
